import type { ComponentType } from 'react';
import { BarChart3, BookOpen, CalendarRange, ClipboardCheck, Clock, Gauge, Home, FileText, Users, ListChecks, Settings, SpellCheck, TrendingUp, Workflow } from 'lucide-react';
import { Role } from '../types/auth';
import { canAccessPath } from './roles';

// The one list of where things live. The sidebar, the group named in a page header, the Guide and the
// 404 page all read it, so a page has one name and one place everywhere. Who may open a page is still
// decided by utils/roles.ts; this file only says how the pages that exist are grouped and called.
//
// Code keeps its old names (Hygiene, Effort, /hygiene, /effort, the services): only the words people
// read change. Hygiene is shown as Naming and Effort as Time logging, as Compliance and My audit
// already call them.

export type NavGroupId = 'today' | 'quarter' | 'reports' | 'standards' | 'admin' | 'help';

export interface NavGroup {
  id: NavGroupId;
  caption: string;
  /** The question a person asks when they reach for this group. */
  question: string;
}

export interface NavItem {
  path: string;
  label: string;
  group: NavGroupId;
  icon: ComponentType<{ className?: string }>;
  /** One plain sentence: what the page is for. */
  purpose: string;
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { id: 'today', caption: 'Today', question: 'What is moving in Jira right now?' },
  { id: 'quarter', caption: 'Quarter', question: 'What is the plan, and how is it going?' },
  { id: 'reports', caption: 'Reports', question: 'What was done, by epic and by person?' },
  { id: 'standards', caption: 'Standards', question: 'Are we following the ways of working we agreed?' },
  { id: 'admin', caption: 'Admin', question: 'How is the portal set up?' },
  { id: 'help', caption: 'Help', question: 'What does this page mean, and where do I find things?' },
];

// Standards run in the order Compliance shows them: Naming, Flow, Time logging, Planning.
const ITEMS = [
  { path: '/home', label: 'Home', group: 'today', icon: Home, purpose: 'Where to start: your quarter, what you have done, what to fix and what changed, and a search for any ticket, person or project.' },
  { path: '/plan', label: 'Plan', group: 'quarter', icon: ListChecks, purpose: "The quarter's plan, project by project: what is picked, what is moving and what is only an idea." },
  { path: '/report', label: 'Report', group: 'quarter', icon: FileText, purpose: 'The mid-month and monthly report, made from Jira, ready to send.' },
  { path: '/reports-overview', label: 'Epic reports', group: 'reports', icon: BarChart3, purpose: 'Every epic in a month, quarter or year: its status, its category and how it got there.' },
  { path: '/my-people', label: 'My people', group: 'reports', icon: Users, purpose: 'The people in your teams, and how their month is going.' },
  { path: '/reports', label: 'Worklogs by person', group: 'reports', icon: TrendingUp, purpose: 'The hours each person logged, day by day, for a month.' },
  { path: '/compliance', label: 'Compliance', group: 'standards', icon: Gauge, purpose: 'How each project is doing against the four standards, month by month.' },
  { path: '/my-audit', label: 'My audit', group: 'standards', icon: ClipboardCheck, purpose: 'Your own items that need a fix, against the four standards.' },
  { path: '/hygiene', label: 'Naming', group: 'standards', icon: SpellCheck, purpose: 'Whether ticket titles are clear enough for the team to read.' },
  { path: '/flow', label: 'Flow', group: 'standards', icon: Workflow, purpose: 'How work moves between people, and where it waits.' },
  { path: '/effort', label: 'Time logging', group: 'standards', icon: Clock, purpose: 'Whether time is logged with the standard tags, and where it is missing.' },
  { path: '/planning', label: 'Planning', group: 'standards', icon: CalendarRange, purpose: 'How far each quarter has come, and how each sprint went.' },
  { path: '/settings', label: 'Settings', group: 'admin', icon: Settings, purpose: 'Connect Jira, set the standards and manage people.' },
  { path: '/guide', label: 'Guide', group: 'help', icon: BookOpen, purpose: 'What each page is for, and what its words mean.' },
] as const;

export type NavPath = (typeof ITEMS)[number]['path'];

export const NAV_ITEMS: readonly NavItem[] = ITEMS;

/** The name of a page, for its header: `navLabel('/effort')` is "Time logging". */
export function navLabel(path: NavPath): string {
  return NAV_ITEMS.find((i) => i.path === path)?.label ?? path;
}

export function navItemFor(pathname: string): NavItem | undefined {
  return NAV_ITEMS.find((i) => pathname === i.path || pathname.startsWith(i.path + '/'));
}

export function navGroupFor(pathname: string): NavGroup | undefined {
  const item = navItemFor(pathname);
  return item ? NAV_GROUPS.find((g) => g.id === item.group) : undefined;
}

/** The Guide: it sits at the foot of the menu, not in a group of its own. */
export const GUIDE_PATH = '/guide';
export const GUIDE_ITEM: NavItem = NAV_ITEMS.find((i) => i.path === GUIDE_PATH) as NavItem;

/** Where a role's access to a page is partial, in a few plain words. */
export const ACCESS_NOTES: Record<string, string> = {
  '/flow': 'Leaders see Waiting only.',
  '/effort': 'Leaders see only the "No time on open work" tab.',
};

/** Who can open a page, in the order admin, user, leader. */
export function rolesWithAccess(path: string): Role[] {
  return (['admin', 'user', 'leader'] as Role[]).filter((r) => canAccessPath(r, path));
}

/**
 * The few pages each role's menu shows first, in order. Every other page the role may open sits under
 * "More pages". Home is always first: it is where a person starts, and what to do next is one press from it.
 */
const PRIMARY: Record<Role, NavPath[]> = {
  user: ['/home', '/my-audit'],
  leader: ['/home', '/plan', '/report'],
  admin: ['/home', '/plan', '/report'],
};

export const MORE_LABEL = 'More pages';

/** The pages a role's menu shows first. */
export function primaryNavForRole(role: Role): NavItem[] {
  return PRIMARY[role]
    .map((p) => NAV_ITEMS.find((i) => i.path === p))
    .filter((i): i is NavItem => !!i && canAccessPath(role, i.path));
}

/** The rest of what a role may open, grouped as in the full menu, for the "More pages" disclosure. */
export function moreNavForRole(role: Role): { group: NavGroup; items: NavItem[] }[] {
  const first = new Set<string>(PRIMARY[role]);
  return navForRole(role)
    .map(({ group, items }) => ({ group, items: items.filter((i) => !first.has(i.path)) }))
    .filter((g) => g.items.length > 0);
}

/** Every page a role may open, grouped in order; empty groups are left out. The Guide is not in it. */
export function navForRole(role: Role): { group: NavGroup; items: NavItem[] }[] {
  return NAV_GROUPS.filter((g) => g.id !== 'help').map((group) => ({
    group,
    items: NAV_ITEMS.filter((i) => i.group === group.id && canAccessPath(role, i.path)),
  })).filter((g) => g.items.length > 0);
}
